import { useState, type FC } from "react";
import { useAuth } from "../../context/AuthContext";
import { useSettings } from "../../context/SettingsContext";

interface TopNavProps {
  onMenuToggle: () => void;
  unreadCount?: number;
  onNotificationBellClick?: () => void;
}

/** Returns up to two uppercase initials from a display name or email. */
function getInitials(displayName: string | null, email: string | null): string {
  if (displayName) {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return displayName.slice(0, 2).toUpperCase();
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return "??";
}

export const TopNav: FC<TopNavProps> = ({
  onMenuToggle,
  unreadCount = 0,
  onNotificationBellClick,
}) => {
  const { user, logout, subdomain } = useAuth();
  const { shopName } = useSettings();
  const [copied, setCopied] = useState(false);

  const currentShopId = subdomain || user?.subdomain || localStorage.getItem("tenantSubdomain") || "shop";
  const initials = getInitials(user?.displayName ?? null, user?.email ?? null);
  const hasUnread = unreadCount > 0;

  const handleCopyShopId = () => {
    if (currentShopId) {
      navigator.clipboard.writeText(currentShopId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 shadow-sm">
      {/* Left: hamburger (mobile) + shop name + Shop ID badge */}
      <div className="flex items-center gap-3">
        {/* Hamburger — visible on mobile/tablet only */}
        <button
          type="button"
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 lg:hidden"
          onClick={onMenuToggle}
          aria-label="Open navigation menu"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
        </button>

        <div className="flex items-center gap-2.5">
          <span className="text-base font-semibold text-gray-800 sm:text-lg">
            {shopName}
          </span>
          {currentShopId && (
            <div className="flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/20">
              <span className="font-bold text-blue-900">Shop ID:</span>
              <code className="font-mono font-bold text-blue-800">{currentShopId}</code>
              <button
                type="button"
                onClick={handleCopyShopId}
                title="Copy Shop ID for Mobile App"
                className="ml-0.5 text-blue-600 hover:text-blue-900 focus:outline-none"
              >
                {copied ? (
                  <span className="text-[10px] font-bold text-green-600">Copied!</span>
                ) : (
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right: notification bell + avatar + logout */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Notification bell */}
        <button
          type="button"
          className="relative rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          onClick={onNotificationBellClick}
          aria-label={
            hasUnread
              ? `Notifications — ${unreadCount} unread`
              : "Notifications"
          }
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.75}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
            />
          </svg>

          {/* Unread badge */}
          {hasUnread && (
            <span
              className="absolute right-0.5 top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white"
              aria-hidden="true"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* User avatar */}
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white"
          title={user?.displayName ?? user?.email ?? "User"}
          aria-label={`Logged in as ${user?.displayName ?? user?.email ?? "User"}`}
        >
          {user?.photoURL ? (
            <img
              src={user.photoURL}
              alt={user.displayName ?? "User avatar"}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            initials
          )}
        </div>

        {/* Logout button */}
        <button
          type="button"
          className="hidden items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:flex"
          onClick={logout}
          aria-label="Log out"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
            />
          </svg>
          Logout
        </button>

        {/* Logout icon-only — mobile */}
        <button
          type="button"
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:hidden"
          onClick={logout}
          aria-label="Log out"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
            />
          </svg>
        </button>
      </div>
    </header>
  );
};
